import { useState, useCallback, useEffect } from 'react';
import { onAIEvent } from '../utils/events';

interface UseEscalationsParams {
  canAdminSystem: boolean;
  sidebarView: string;
  userId: number | undefined;
}

interface UseEscalationsReturn {
  escalations: any[];
  escalationsTotal: number;
  escalationsLoading: boolean;
  fetchEscalations: (status?: string) => Promise<void>;
  claimEscalation: (id: string) => Promise<void>;
  resolveEscalation: (id: string) => Promise<void>;
  transferEscalation: (id: string, target: { to_agent_id?: number; to_department?: string }, note?: string) => Promise<void>;
  addEscalationNote: (id: string, note: string) => Promise<void>;
}

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const authHeaderJson = () => ({ ...authHeader(), 'Content-Type': 'application/json' });

export function useEscalations({
  canAdminSystem,
  sidebarView,
  userId,
}: UseEscalationsParams): UseEscalationsReturn {
  const [escalations, setEscalations] = useState<any[]>([]);
  const [escalationsTotal, setEscalationsTotal] = useState(0);
  const [escalationsLoading, setEscalationsLoading] = useState(false);

  const fetchEscalations = useCallback(async (status?: string) => {
    if (!canAdminSystem) return;
    setEscalationsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (status) params.set('status', status);
      const res = await fetch(`/api/ai/escalations?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setEscalations(data.escalations || []);
        setEscalationsTotal(data.total || 0);
      }
    } catch (err) {
      console.warn('Failed to fetch escalations:', err);
    } finally {
      setEscalationsLoading(false);
    }
  }, [canAdminSystem]);

  const claimEscalation = useCallback(async (id: string) => {
    try {
      await fetch(`/api/ai/escalations/${id}/claim`, { method: 'PUT', headers: authHeader() });
      setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: 'claimed', claimed_by: userId } : e));
    } catch (err) {
      console.warn('Failed to claim escalation:', err);
    }
  }, [userId]);

  const resolveEscalation = useCallback(async (id: string) => {
    try {
      await fetch(`/api/ai/escalations/${id}/resolve`, {
        method: 'PUT', headers: authHeaderJson(), body: '{}'
      });
      setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: 'resolved' } : e));
    } catch (err) {
      console.warn('Failed to resolve escalation:', err);
    }
  }, []);

  const transferEscalation = useCallback(async (id: string, target: { to_agent_id?: number; to_department?: string }, note?: string) => {
    try {
      await fetch(`/api/ai/escalations/${id}/transfer`, {
        method: 'PUT', headers: authHeaderJson(), body: JSON.stringify({ ...target, note })
      });
      setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: target.to_department ? 'pending' : 'claimed', route: target.to_department || e.route } : e));
    } catch (err) {
      console.warn('Failed to transfer escalation:', err);
    }
  }, []);

  const addEscalationNote = useCallback(async (id: string, note: string) => {
    try {
      await fetch(`/api/ai/escalations/${id}/notes`, {
        method: 'POST', headers: authHeaderJson(), body: JSON.stringify({ note })
      });
    } catch (err) {
      console.warn('Failed to add note:', err);
    }
  }, []);

  // Auto-fetch escalations when switching to that view
  useEffect(() => {
    if (sidebarView === 'escalations' && canAdminSystem) {
      fetchEscalations();
    }
  }, [sidebarView, canAdminSystem, fetchEscalations]);

  // Real-time escalation updates via typed WebSocket events
  useEffect(() => {
    if (!canAdminSystem) return;

    const unsubs = [
      onAIEvent('escalationNew', (esc) => {
        setEscalations(prev => [{ ...esc, status: 'pending' }, ...prev]);
        setEscalationsTotal(prev => prev + 1);
      }),
      onAIEvent('escalationClaimed', ({ id, claimed_by }) => {
        setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: 'claimed', claimed_by } : e));
      }),
      onAIEvent('escalationRerouted', ({ id, rerouted_to, rerouted_at }) => {
        setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: 'rerouted', rerouted_to, rerouted_at } : e));
      }),
      onAIEvent('escalationResolved', ({ id, resolved_by }) => {
        setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: 'resolved', resolved_by } : e));
      }),
      onAIEvent('escalationTransferred', ({ id, to_agent, department, status }) => {
        setEscalations(prev => prev.map(e => e.id === id ? { ...e, status: status || 'pending', route: department || e.route, claimed_by: to_agent || null } : e));
      }),
    ];

    return () => unsubs.forEach(unsub => unsub());
  }, [canAdminSystem, sidebarView]);

  return {
    escalations,
    escalationsTotal,
    escalationsLoading,
    fetchEscalations,
    claimEscalation,
    resolveEscalation,
    transferEscalation,
    addEscalationNote,
  };
}
